import {Component, OnInit} from '@angular/core';
import {ApiMode, ConfigService, VaultProfile} from '@shared-library';
import {AuthService, CookieProbeScope} from '../../services/auth.service';
import {Router, RouterModule} from '@angular/router';
import {NGXLogger} from 'ngx-logger';
import {CommonModule} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {environment} from "../../../environments/environment";
import {v4 as uuidv4} from "uuid";
import {FASTEN_CONNECT_OAUTH_CONTEXT_CACHE_KEY_PREFIX} from "../../utils/oauth2_callback_context";
import {TEST_IDENTITY_EMAILS} from "../../utils/test-identities";
import {FdkAlertComponent, FdkButtonComponent, FdkInputComponent, FdkSpinnerComponent} from "fasten-design-kit";
import {BehaviorSubject} from 'rxjs';
import {FastenService} from '../../services/fasten.service';
import {SentryContextService} from '../../services/sentry-context.service';


@Component({
    selector: 'app-auth-signin',
    templateUrl: './vault-profile-signin.component.html',
    styleUrls: ['./vault-profile-signin.component.css'],
    standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    FdkSpinnerComponent,
    FdkAlertComponent,
    FdkButtonComponent,
    FdkInputComponent
  ]
})
export class VaultProfileSigninComponent implements OnInit {
  loading: boolean = false
  showMessage: boolean = false

  submitted: boolean = false
  existingVaultProfile: VaultProfile = new VaultProfile()
  errorMsg: string = ""
  message: string = ""
  readonly testIdentityEmails = TEST_IDENTITY_EMAILS;
  needStorageAccessPermissionSubject = new BehaviorSubject(false)
  userInteractionWindowOpened = false
  userInteractionCompletedSubject = new BehaviorSubject(false)

  get isCspRequestUriSignin(): boolean {
    return !!(this.configService.systemConfig$?.tefcaMode && this.configService.systemConfig$?.identityRequestUri);
  }


  constructor(
    public configService: ConfigService,
    private authService: AuthService,
    private fastenService: FastenService,
    private router: Router,
    private logger: NGXLogger,
    private sentryContext: SentryContextService,
  ) { }

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    this.setMessage(urlParams.get('action') || "")
    if (this.configService.vaultProfileConfig$.email) {
      this.existingVaultProfile.email = this.configService.vaultProfileConfig$.email
    }

    if (!this.authService.RequiresStorageAccessFallback()) {
      this.logger.log("Storage Access API fallback is not required or is unavailable.");
      this.needStorageAccessPermissionSubject.next(false);
    } else {
      this.hasStorageAccess().then((hasAccess) => {
        this.needStorageAccessPermissionSubject.next(!hasAccess);
      })
    }
  }

  signinSubmit(){
    this.submitted = true;
    this.loading = true
    this.errorMsg = ""
    this.configService.vaultProfileConfig = {
      identityVerificationHandledForSession: false,
    }

    if (this.isCspRequestUriSignin) {
      this.signinWithCspRequestUri();
      return;
    }

    this.configService.vaultProfileConfig = {
      email: this.existingVaultProfile.email,
    };

    // Safari requires requestStorageAccess to run from the submit gesture.
    const storageAccessPromise = this.authService.RequiresStorageAccessFallback()
      ? this.requestStorageAccess()
      : Promise.resolve(true)
    const signoutPromise = this.authService.Signout()

    Promise.all([signoutPromise, storageAccessPromise]).then(() => {
        this.logger.info("Signin", this.existingVaultProfile.email)
        return this.authService.VaultAuthBegin(this.existingVaultProfile.email!, this.configService.systemConfig$.tefcaCspPromptForce)
      })
      .then(async (resp) => {

        // Test identities may receive a session before entering a code.
        if (this.configService.systemConfig$.apiMode === ApiMode.Test && !await this.authService.WaitForVaultAuthSession()) {
          this.loading = false
          return this.router.navigateByUrl('auth/signin/cookies-required')
        }
        this.loading = false
        return this.navigateToCodePage(resp?.expires)
      })
      .catch((err)=>{
        this.loading = false
        this.errorMsg = this.deriveSignInErrorMessage(err)
        this.logger.error("Sign-in failed with user message: ", this.errorMsg, "; And error: ", err)
      })
  }

  private navigateToCodePage(codeExpiresAt?: number): Promise<boolean> {
    const queryParams: {currentEmail?: string, codeExpiresAt?: number} = {
      currentEmail: this.existingVaultProfile.email,
    }
    if (codeExpiresAt) {
      queryParams.codeExpiresAt = codeExpiresAt
    }
    return this.router.navigate(['auth/signin/code'], {queryParams})
  }

  async signinWithCspRequestUri(): Promise<void> {
    try {
      const authorizeUrl = await this.buildCspRequestUriAuthorizeUrl();
      window.location.assign(authorizeUrl);
    } catch (err) {
      this.loading = false;
      this.logger.error("failed to start CSP request_uri signin", err);
      this.errorMsg = "An unknown error occurred during sign-in.";
    }
  }

  async buildCspRequestUriAuthorizeUrl(): Promise<string> {
    const systemConfig = this.configService.systemConfig$;
    if (!systemConfig.tefcaMode || !systemConfig.identityRequestUri) {
      throw new Error("identity-request-uri sign-in requires tefca-mode.");
    }

    const state = uuidv4();
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const redirectUri = this.getCallbackUrl();
    const authorizeUrl = new URL(`${environment.identity_api_endpoint_base}/oauth2/authorize`);

    localStorage.setItem(`${FASTEN_CONNECT_OAUTH_CONTEXT_CACHE_KEY_PREFIX}${state}`, JSON.stringify({
      codeVerifier,
      systemConfig,
      vaultProfileConfig: this.configService.vaultProfileConfig$,
      searchConfig: this.configService.searchConfig$,
    }));

    authorizeUrl.searchParams.set('response_type', 'code');
    authorizeUrl.searchParams.set('client_id', systemConfig.publicId);
    authorizeUrl.searchParams.set('redirect_uri', redirectUri);
    authorizeUrl.searchParams.set('state', state);
    authorizeUrl.searchParams.set('code_challenge', codeChallenge);
    authorizeUrl.searchParams.set('code_challenge_method', 'S256');
    authorizeUrl.searchParams.set('request_uri', systemConfig.identityRequestUri);

    return authorizeUrl.toString();
  }

  private getCallbackUrl(): string {
    if (environment.connect_base_domain === 'localhost') {
      return `${window.location.origin}/auth/callback`;
    }

    return `https://${environment.connect_base_domain}/auth/callback`;
  }

  private generateCodeVerifier(): string {
    const randomBytes = new Uint8Array(32);
    window.crypto.getRandomValues(randomBytes);
    return this.base64UrlEncode(randomBytes);
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encodedVerifier = new TextEncoder().encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', encodedVerifier);
    return this.base64UrlEncode(new Uint8Array(digest));
  }

  private base64UrlEncode(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => binary += String.fromCharCode(byte));
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private deriveSignInErrorMessage(err: any): string {
    const status = typeof err?.status === 'number' ? err.status : undefined;
    if (status === 401 || status === 403) {
      return "email or password is incorrect";
    }
    if (status === 0 || (status !== undefined && status >= 500)) {
      return "An internal server error occurred. Please try again.";
    }
    if (typeof err?.error === 'string') {
      return err.error;
    }
    if (typeof err?.error?.message === 'string') {
      return err.error.message;
    }
    if (typeof err?.error?.error === 'string') {
      return err.error.error;
    }
    if (typeof err?.message === 'string') {
      return err.message;
    }
    return "An unknown error occurred during sign-in.";
  }

  setMessage(action: string) {
    if (action === "email-changed"){
      this.showMessage = true;
      this.message = "Email successfully changed! Please sign in with your new email.";
    } else if (action === "password-changed"){
      this.showMessage = true;
      this.message = "Password successfully changed! Please sign in with your new password.";
    }
  }

  isStorageAccessApiSupportedByBrowser(): boolean {
    const canCheckStorageAccess = typeof document.hasStorageAccess === 'function';
    const canRequestStorageAccess = typeof document.requestStorageAccess === 'function';
    return canCheckStorageAccess && canRequestStorageAccess;
  }

  hasStorageAccess(): Promise<boolean> {
    if (!this.isStorageAccessApiSupportedByBrowser()) {
      this.logger.warn("Storage Access API not available in this browser.");
      this.sentryContext.recordStorageAccessResult('unavailable')
      return Promise.resolve(true)
    }

    return document.hasStorageAccess()
      .then((hasAccess) => {
        this.logger.log("Storage Access API unpartitioned or already granted!", hasAccess);
        return hasAccess
      })
      .catch((error) => {
        this.logger.error("Storage access has not been granted", error);
        return false;
      });
  }

  requestStorageAccess(): Promise<boolean> {
    if (!this.isStorageAccessApiSupportedByBrowser()) {
      this.logger.warn("Storage Access API not available in this browser.");
      return Promise.resolve(true)
    }

    return document.requestStorageAccess()
      .then(async () => {
        this.logger.log("Storage access granted!");

        // Repeat the same fresh, correlated cookie probe used during startup now
        // that the browser has granted unpartitioned storage access.
        let cookieSupported: boolean
        try {
          cookieSupported = await this.authService.CheckCookieSupport(CookieProbeScope.Regular)
        } catch (error) {
          this.sentryContext.recordStorageAccessResult('verification_error')
          throw error
        }
        if (!cookieSupported) {
          const error = new Error("Storage access was granted, but cookies are still blocked. Please allow cookies and try again.")
          this.sentryContext.recordStorageAccessResult('granted_but_cookies_blocked')
          this.restoreFirstPartyConsent()
          this.logger.error("Storage access cookie verification failed", error)
          throw error
        }

        this.sentryContext.recordStorageAccessResult('granted')
        return true;
      }, (error: unknown) => {
        this.sentryContext.recordStorageAccessResult('denied')
        this.restoreFirstPartyConsent()
        this.logger.log("Storage access denied by user", error);
        alert("Cookies are required for the Fasten widget to function. Please allow storage access to continue.");
        return Promise.reject(error)
      })
  }

  private restoreFirstPartyConsent(): void {
    this.needStorageAccessPermissionSubject.next(true)
    this.userInteractionCompletedSubject.next(false)
    this.userInteractionWindowOpened = false
  }

  awaitUserInteractionCompleted(): void {
    if (!this.needStorageAccessPermissionSubject.getValue()) {
      this.logger.log("No storage access required, no user interaction needed.");
      return;
    }
    if (this.userInteractionCompletedSubject.getValue()) {
      this.logger.log("User interaction already completed, no need to wait.");
      return;
    }
    if (this.userInteractionWindowOpened) {
      this.logger.log("User interaction window already opened, waiting for completion.");
      return
    }

    this.userInteractionWindowOpened = true
    this.fastenService.storageApiUserInteractionWithPopup().subscribe((result) => {
      this.logger.log("User interaction completed", result);
      this.userInteractionCompletedSubject.next(result.success)
      this.userInteractionWindowOpened = false
    })
  }

  protected readonly ApiMode = ApiMode;
}
